<template>
  <div
    v-if="data"
    class="flex grow flex-col overflow-hidden"
  >
    <div v-if="isLoggedIn">
      <div class="grid grid-cols-2 border-b px-6 py-4 text-sm font-medium lg:hidden">
        <a
          class="flex items-center justify-center space-x-2 rounded-md px-4 py-2"
          :class="[activeMobileTab === 'view' ? 'cursor-default bg-slate-100 text-slate-500 hover:text-slate-500' : 'cursor-pointer hover:bg-slate-100']"
          @click="activeMobileTab = 'view'"
        >
          {{ $t("your_link") }}
        </a>
        <a
          class="flex items-center justify-center space-x-2 rounded-md px-4 py-2"
          :class="[activeMobileTab === 'edit' ? 'cursor-default bg-slate-100 text-slate-500 hover:text-slate-500' : 'cursor-pointer hover:bg-slate-100']"
          @click="activeMobileTab = 'edit'"
        >
          {{ $t("personalize") }}
        </a>
      </div>
    </div>

    <div class="flex grow overflow-y-auto overflow-x-hidden lg:overflow-hidden">
      <div
        v-if="isLoggedIn"
        class="flex w-full shrink-0 flex-col lg:hidden"
        :class="{ hidden: activeMobileTab === 'view' }"
      >
        <!-- Mobile only; on desktop the partner layout shows CopilotEditorSidebar -->
        <LazyCopilotLiveEditor
          v-model:first-name="data.advisor.first_name"
          v-model:last-name="data.advisor.last_name"
          v-model:phone="data.advisor.phone"
          v-model:logo="data.advisor.logo"
          v-model:personal-photo="data.advisor.personal_photo"
        />
      </div>

      <div
        class="flex grow flex-col overflow-y-auto transition-spacing"
        :class="{
          'hidden lg:flex': activeMobileTab === 'edit'
        }"
      >
        <CopilotBand
          :partner="data.partner"
          :advisor="isLoggedIn ? data.advisor : undefined"
          align="start"
          heading-type="h1"
          is-brand-voice
          has-benefits-below
          :eyebrow="$t('make_our_website_yours', 'Make our website yours')"
          :description="description"
          class="pb-20 md:pb-24 lg:pb-28"
        >
          <template #top>
            <ToggleSwitch
              v-if="isLoggedIn"
              v-model="isEditorOpen"
              :label="$t('edit_your_details', 'Edit your details')"
              size="sm"
              class="mb-8 hidden font-semibold lg:block"
            />
          </template>

          <template #title>
            <template v-if="headingParts">
              {{ headingParts[0] }}<span class="unstyled lg:block">{{ data.partner.name }}{{ headingParts[1] }}</span>
            </template>
            <template v-else>
              {{ heading }}
            </template>
          </template>

          <div
            class="rounded-lg bg-white shadow-sm"
          >
            <!-- The tabs and the panel draw the card's border between them, so the active tab's line is its top edge -->
            <div
              role="tablist"
              class="grid grid-cols-2"
            >
              <!-- The inactive tab draws the divider, so it never meets the active tab's line at an angle -->
              <button
                v-for="mode in linkModes"
                :key="mode.value"
                type="button"
                role="tab"
                :aria-selected="linkMode === mode.value"
                class="flex items-center justify-center gap-2 whitespace-nowrap px-3 py-3 text-sm font-semibold transition-colors first:rounded-tl-lg first:border-l last:rounded-tr-lg last:border-r"
                :class="linkMode === mode.value
                  ? 'border-t-2 border-t-brand-500 bg-white text-brand'
                  : 'border-b border-t bg-gray-50 text-supplement first:border-r last:border-l hover:bg-gray-100 hover:text-paragraph'"
                @click="linkMode = mode.value"
              >
                <Icon
                  :icon="mode.icon"
                  prefix="far"
                />
                {{ mode.label }}
              </button>
            </div>

            <div class="rounded-b-lg border-x border-b p-6 sm:p-7">
              <div
                v-if="linkMode === 'page'"
                class="mb-6"
              >
                <InputLabel name="webpage_url">
                  {{ $t('cobrand_website_page_url_paste', 'Paste a link to any page on :domain').replace(':domain', partnerDomain ?? '') }}
                </InputLabel>
                <div class="relative mt-1.5">
                  <input
                    id="webpage_url"
                    v-model="webpageUrl"
                    name="webpage_url"
                    type="url"
                    :placeholder="`https://${homepageDomain}/…`"
                    class="form-input w-full rounded-md border-none py-3 pl-4 pr-10 text-sm ring-1 ring-inset focus:ring-primary"
                    :class="isPageUrlInvalid ? 'ring-red-500 focus:ring-red-500' : 'ring-input'"
                    :aria-invalid="isPageUrlInvalid"
                    aria-describedby="webpage_url_error"
                  />
                  <button
                    v-if="webpageUrl"
                    type="button"
                    :title="$t('clear', 'Clear')"
                    class="absolute inset-y-0 right-0 px-4 text-gray-400 hover:text-heading"
                    @click="webpageUrl = ''"
                  >
                    <Icon icon="times" />
                  </button>
                </div>
                <p
                  v-if="isPageUrlInvalid"
                  id="webpage_url_error"
                  class="mt-1.5 text-xs text-red-600"
                >
                  {{ $t('cobrand_website_page_invalid', 'This link isn\'t on :domain. Paste a link to a page on :domain.').replaceAll(':domain', partnerDomain ?? '') }}
                </p>
              </div>

              <InputLabel name="cobranded_link">
                {{ $t('cobrand_website_your_link', 'Your link') }}
              </InputLabel>
              <a
                v-if="isLoggedIn && cobrandedLinkParts"
                id="cobranded_link"
                :href="cobrandedLink"
                :title="$t('view')"
                target="_blank"
                rel="noopener"
                class="group mt-1.5 block break-all font-semibold text-brand underline decoration-brand/30 underline-offset-4 hover:decoration-brand"
              >{{ cobrandedLinkParts[0] }}<span class="rounded bg-amber-100 px-1 text-amber-800 underline decoration-amber-400 underline-offset-4 group-hover:decoration-amber-700">{{ cobrandedLinkParts[1] }}</span>{{ cobrandedLinkParts[2] }}</a>
              <!-- Not a link until it's theirs, so it isn't mistaken for one to share -->
              <p
                v-else-if="cobrandedLinkParts"
                id="cobranded_link"
                class="mt-1.5 select-none break-all font-semibold text-heading"
              >
                {{ cobrandedLinkParts[0] }}<span class="rounded bg-amber-100 px-1 text-amber-800">{{ cobrandedLinkParts[1] }}</span>{{ cobrandedLinkParts[2] }}
              </p>
              <p
                v-else
                id="cobranded_link"
                class="mt-1.5 text-supplement"
              >
                {{ $t('cobrand_website_link_pending', 'Enter a page above to get your link') }}
              </p>
              <p
                v-if="isLoggedIn && cobrandedLink"
                class="mt-2 flex items-center gap-1.5 text-xs text-supplement"
              >
                <Icon
                  icon="circle-info"
                  prefix="far"
                  class="shrink-0"
                />
                {{ $t('cobrand_website_insider_hint', 'The highlighted part is your advisor ID, so every lead comes to you.') }}
              </p>

              <template v-if="isLoggedIn">
                <InputLabel :ui="{ label: 'mt-6' }">
                  {{ $t('share_with_your_clients', 'Share with your clients') }}
                </InputLabel>

                <!-- Fits the space the card has: the icons shrink to a point, then move above Copy link -->
                <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-3">
                  <div class="grid max-w-[19rem] grow-[100] basis-60 grid-cols-6 gap-2">
                    <Button
                      v-for="option in shareOptions"
                      :key="option.value"
                      :icon="option.icon"
                      :icon-prefix="option.prefix"
                      :title="option.label"
                      :disabled="!cobrandedLink"
                      :ui="{ base: `aspect-square w-full p-0 text-base shadow-none ${option.classes}` }"
                      @click="openShareOption(option.value)"
                    />
                  </div>
                  <Button
                    :prepend-icon="cobrandedLinkCopied ? 'check' : 'link'"
                    prepend-icon-prefix="fas"
                    :disabled="!cobrandedLink"
                    :ui="{ base: 'inline-flex h-11 grow items-center justify-center whitespace-nowrap bg-amber-500 py-0 text-white shadow-none hover:bg-amber-100 hover:text-amber-500' }"
                    @click="copyCobrandedLink()"
                  >
                    {{ cobrandedLinkCopied ? $t('copied') : $t('copy_link') }}
                  </Button>
                </div>
              </template>

              <template v-else>
                <InputLabel
                  name="email"
                  :ui="{ label: 'mt-6' }"
                >
                  {{ $t('cobrand_website_sign_up_label', 'Sign up or sign in to get your personalized link, so every lead comes to you') }}
                </InputLabel>
                <LoginOrRegisterForm
                  :button-label="$t('get_my_website', 'Get my website')"
                  source="brand-copilot"
                  :partner-slug="partner_slug"
                  :redirect-to="redirectTo"
                  button-color="brand"
                  inline
                  class="mt-1.5"
                  @success="onSuccess"
                />

                <template v-if="display === 'form'">
                  <InputLabel :ui="{ label: 'mt-6' }">
                    {{ $t('share_with_your_clients', 'Share with your clients') }}
                  </InputLabel>
                  <!-- Choosing one explains what it does once signed up, without sharing anything -->
                  <div class="mt-2 grid max-w-[19rem] grid-cols-6 gap-2">
                    <Button
                      v-for="option in shareOptions"
                      :key="option.value"
                      :icon="option.icon"
                      :icon-prefix="option.prefix"
                      :title="option.label"
                      :disabled="!cobrandedLink"
                      :ui="{ base: `aspect-square w-full p-0 text-base shadow-none ${option.classes}` }"
                      @click="openShareSignUp(option.value)"
                    />
                  </div>

                  <Markdown
                    as="p"
                    class="mt-6 text-xs text-supplement"
                    :text="$t('enter_email_legal')"
                  />
                </template>
              </template>
            </div>
          </div>
        </CopilotBand>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LoginOrRegisterResponse } from '~/components/forms/LoginOrRegisterForm.vue'
import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { withQuery } from 'ufo'
import type { Media } from '~/types/media'
import type { ShareOption } from '~/types/partner'
import type { LocationQueryRaw } from 'vue-router'

export type CopilotPage = {
  partner: {
    readonly hashid: string
    name: string
    logo: Media | null
    site: {
      domain: string
      featured_image: Media | null
      link_preview_image_url: string | null
    }
    copilot_advisor_branding: 'logo' | 'personal-photo' | 'display-name' | null
    styles: Record<string, string>
    copilot_page_domains: string[]
  }
  advisor: {
    hashid: string | null
    first_name: string | null
    last_name: string | null
    email: string
    phone: string | null
    logo: Media | null
    personal_photo: Media | null
  }
}

const { backendUrl } = useRuntimeConfig().public
const { isLoggedIn, user } = useAuth()
const { t } = useI18n()
const { partner_slug } = useRoute().params
const { data, refresh } = await useAsyncData(`copilot-${partner_slug}`, async () => await $larafetch<CopilotPage>(`copilot/${partner_slug}`), {
  deep: true
})

if (!data.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

// Mobile tabs
const activeMobileTab = ref<'view' | 'edit'>('view')

// Co-branded links
// Guests see the placeholder the preview uses until they have an advisor ID of their own
const insiderParam = computed(() => isLoggedIn.value && data.value ? getInsiderParam(data.value.advisor) : 'your-name')

const homepageDomain = computed(() => data.value ? getCopilotDomain(data.value.partner.site.domain) : '')

const homepageLink = computed(() => {
  return withQuery(`https://${homepageDomain.value}`, { insider: insiderParam.value })
})

const linkMode = ref<'homepage' | 'page'>('homepage')
const linkModes = computed<{ value: 'homepage' | 'page', label: string, icon: IconName }[]>(() => [
  { value: 'homepage', label: t('homepage', 'Homepage'), icon: 'house' },
  { value: 'page', label: t('specific_page', 'Specific page'), icon: 'link' }
])

const webpageUrl = ref('')

const pageUrl = computed(() => {
  const url = webpageUrl.value.trim()
  return url && data.value ? getCopilotPageUrl(url, data.value.partner.copilot_page_domains) : null
})
const isPageUrlInvalid = computed(() => linkMode.value === 'page' && !!webpageUrl.value.trim() && !pageUrl.value)

const cobrandedLink = computed(() => {
  if (linkMode.value === 'homepage') return homepageLink.value

  return pageUrl.value ? withQuery(pageUrl.value, { insider: insiderParam.value }) : ''
})
// Highlights the insider parameter, as the address bar in the preview does
const cobrandedLinkParts = computed(() => {
  const match = cobrandedLink.value.match(/^(.*?)([?&]insider=[^&#]*)(.*)$/)

  return match ? [match[1], match[2], match[3]] : null
})
const { copy: copyCobrandedLink, copied: cobrandedLinkCopied } = useClipboard({
  source: cobrandedLink
})

// Share
const shareOptions = computed<{ value: ShareOption, label: string, icon: IconName, prefix: IconPrefix, classes: string }[]>(() => [
  { value: 'facebook', label: 'Facebook', icon: 'facebook', prefix: 'fab', classes: 'bg-blue-100 text-blue-500 hover:bg-blue-500' },
  { value: 'instagram', label: 'Instagram', icon: 'instagram', prefix: 'fab', classes: 'bg-pink-100 text-pink-500 hover:bg-pink-500' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'linkedin', prefix: 'fab', classes: 'bg-[#dcedfd] text-[#0a66c2] hover:bg-[#0a66c2]' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp', prefix: 'fab', classes: 'bg-[#b1f1c9] text-[#1b994a] hover:bg-[#25d366] hover:text-white' },
  { value: 'email', label: t('email'), icon: 'envelope', prefix: 'fas', classes: 'bg-purple-100 text-purple-500 hover:bg-purple-500' },
  { value: 'qrcode', label: t('qr_code'), icon: 'qrcode', prefix: 'fas', classes: 'bg-teal-100 text-teal-500 hover:bg-teal-500' }
])

const { openModal } = useModal()

const ShareSignUpModal = resolveComponent('LazyShareSignUpModal')

const openShareSignUp = (option: ShareOption) => {
  openModal(ShareSignUpModal, {
    option,
    partnerSlug: partner_slug,
    partnerName: data.value?.partner.name,
    redirectTo: redirectTo.value,
    previewImage: data.value?.partner.site.featured_image,
    previewImageUrl: data.value?.partner.site.link_preview_image_url,
    previewDomain: partnerDomain.value,
    previewLink: cobrandedLink.value.replace(/^https?:\/\//, '') || partnerDomain.value
  })
}

const ShareModal = resolveComponent('LazyShareModal')

const openShareOption = (option: ShareOption) => {
  // Guards against a disabled option being reached
  if (!data.value || !user.value || !cobrandedLink.value) return

  const sharedPageUrl = linkMode.value === 'page' ? pageUrl.value : null

  openModal(ShareModal, {
    landingPageUrl: cobrandedLink.value,
    partnerName: data.value.partner.name,
    embedCode: '',
    socialShareText: null,
    activeOption: option,
    aiEndpoint: `ai/websites/${partner_slug}`,
    aiBody: { url: sharedPageUrl },
    options: shareOptions.value.map(({ value }) => value),
    facebookMode: 'link',
    qrUrl: withQuery(`${backendUrl}/users/${user.value.hashid}/copilot/${data.value.partner.hashid}/qr`, { url: sharedPageUrl || undefined }),
    mediaEndpoint: withQuery(`websites/${partner_slug}/pages/share-images`, { url: sharedPageUrl || undefined })
  })
}

// Gets the page's details ready in the background, so sharing it doesn't wait on them
const prefetchedPageUrls = new Set<string>()

watchDebounced(pageUrl, (url) => {
  if (!url || !isLoggedIn.value || prefetchedPageUrls.has(url)) return

  prefetchedPageUrls.add(url)
  $larafetch(`websites/${partner_slug}/pages/prefetch`, { method: 'POST', body: { url } }).catch(() => {})
}, { debounce: 500 })

// Hero
// Spacing comes from the translation, as some languages don't use spaces
const heading = computed(() => t('cobrand_website_share_heading', 'Share your co-branded :partnerName website'))
const headingParts = computed(() => {
  const parts = heading.value.split(':partnerName')

  return parts.length === 2 ? parts : null
})
const description = computed(() => t('cobrand_website_share_subheading', 'Create a personalized link to our website and share it with your clients in just a few clicks.'))
const partnerDomain = computed(() => data.value?.partner.site.domain.replace(/^www\./, ''))

// First name and phone are the minimum an advisor needs for a useful co-branded bar
const hasAdvisorDetails = computed(() => !!(data.value?.advisor.first_name?.trim() && (data.value?.advisor.logo || data.value?.advisor.personal_photo)))

// Decided once on load so the editor never closes while being filled in
const isEditorOpen = useCopilotEditorOpen()
isEditorOpen.value = !hasAdvisorDetails.value

// Brands can link advisors to ?tab=specific or ?url=…, often with the link unencoded
const route = useRoute()
const router = useRouter()

// Never prefill a lookalike URL that could pass as the partner's
const isPartnerUrl = (url: string): boolean => !!data.value && !!getCopilotPageUrl(url, data.value.partner.copilot_page_domains)

const linkQuery = computed(() => parseLinkQuery(route.fullPath))

const applyLinkQuery = () => {
  const { url, query: { tab } } = linkQuery.value
  if (tab !== 'specific' && url === undefined) return

  linkMode.value = 'page'

  if (typeof url === 'string' && isPartnerUrl(url)) {
    webpageUrl.value = url
  }
}

const removeLinkQuery = () => {
  const { url, query: { tab, ...query } } = linkQuery.value
  if (tab !== undefined || url !== undefined) {
    router.replace({ query: query as LocationQueryRaw })
  }
}

applyLinkQuery()
onMounted(removeLinkQuery)

// Signing up returns visitors here with the parameters again
watch(() => route.fullPath, () => {
  applyLinkQuery()
  removeLinkQuery()
})

const redirectTo = computed(() => {
  const path = `/${partner_slug}/website`

  if (linkMode.value !== 'page') return path
  if (webpageUrl.value) return withQuery(path, { url: webpageUrl.value })

  return withQuery(path, { tab: 'specific' })
})

const display = ref<'form' | 'new_user' | 'existing_user'>('form')
const onSuccess = ({ new_user }: LoginOrRegisterResponse) => {
  display.value = new_user ? 'new_user' : 'existing_user'
  refresh()
}

// Head
const { fullPath } = useRoute()
const { fbAppId, frontendUrl, siteName } = useRuntimeConfig().public

const head = {
  title: `Your ${data.value.partner.name} Co-Branded Website`,
  description: `Your ${data.value.partner.name} co-branded website. Powered by ${siteName}.`,
  image: data.value.partner.site.featured_image?.full_url || ''
}

definePageMeta({
  source: 'brand-copilot'
})

useHead({
  title: head.title,
  meta: [
    // General
    { name: 'description', content: head.description },
    // OpenGraph
    { property: 'fb:app_id', content: fbAppId },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: frontendUrl + fullPath },
    { property: 'og:title', content: head.title },
    { property: 'og:description', content: head.description },
    { property: 'og:image', content: head.image },
    { property: 'og:locale', content: 'en' },
    { property: 'og:site_name', content: siteName },
    // Twitter Card
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: head.title },
    { name: 'twitter:description', content: head.description },
    { name: 'twitter:image', content: head.image }
  ]
})
</script>
